<template>
  <ion-list
    ref="listRef"
    v-on-click-outside="onClickOutside"
    :style="{
      '--ion-item-background': 'var(--rf-bg-elevation-2)',
    }"
  >
    <IonItemSliding
      v-for="(item, index) in props.items"
      :key="item._id"
      :disabled="selectMode"
    >
      <IonItem
        :style="{
          '--background': 'var(--rf-bg-elevation-2)',
          '--border-color': 'var(--rf-border-elevation-2-secondary)',
        }"
        lines="inset"
        class="item"
      >
        <ion-checkbox
          v-if="selectMode"
          slot="start"
          :checked="moveItemsIsSelected(item._id)"
          @ion-change="onSelectToggle(item._id)"
          @click.stop
        />
        <div class="w-full">
          <OrderModalHomeMenuitemsListItem
            :title="item.title"
            :quantity="item.quantity"
            :subtitle="item.subtitle"
            :comment="item.comment"
            :variations="item.variations"
            :extras="item.extras"
            :removables="item.removables"
            :status="item.status"
            class="w-full"
            @click="onRowClick(item)"
            @double-click="onDoubleClick(item)"
          />
        </div>
      </IonItem>
      <IonItemOptions
        v-if="props.quantityChangable"
        side="start"
        :style="{
          'border-bottom-color': 'var(--rf-border-elevation-2-secondary)',
        }"
        @ionSwipe="onFullSwipe(item)"
      >
        <template v-if="props.quantityChangable">
          <IonItemOption
            class="bg-bg-elevation-2 relative"
            @click="emit('decrease-quantity', index)"
          >
            <div
              class="bg-bg-blue absolute top-0 left-0 w-full h-full opacity-20"
            />
            <AppIcon
              icon="minus"
              stroke-width="1.8"
              color="text-fg-blue"
              class="w-[24px] h-[24px] relative pointer-events-none"
            />
          </IonItemOption>
          <IonItemOption
            class="bg-bg-blue"
            @click="emit('increase-quantity', index)"
          >
            <AppIcon
              icon="plus"
              stroke-width="1.8"
              color="text-universal-fg-opposite"
              class="w-[24px] h-[24px] pointer-events-none"
            />
          </IonItemOption>
        </template>
        <IonItemOption
          v-if="props.quantityChangable"
          class="bg-bg-elevation-2 px-2"
          @click="onAddComment(index)"
        >
          <AppIcon
            icon="message-text"
            stroke-width="1.8"
            color="text-fg-elevation-2-secondary"
            class="w-[22px] h-[22px] pointer-events-none relative"
          />
        </IonItemOption>
        <!-- <IonItemOption
          v-if="isStatusChangable(item)"
          class="bg-bg-blue"
          expandable
          @click="onStatusChange(item, $event)"
        >
          <AppIcon
            icon="check"
            stroke-width="2"
            color="text-fg-white"
            class="w-[22px] h-[22px] pointer-events-none relative"
          />
        </IonItemOption> -->
      </IonItemOptions>
      <IonItemOptions
        v-if="props.deletable"
        side="end"
        :style="{
          'border-bottom-color':
            props.items?.length === index + 1
              ? 'transparent'
              : 'var(--rf-border-elevation-2-secondary)',
        }"
      >
        <IonItemOption class="bg-bg-red" @click="onDelete(item, $event)">
          <AppIcon
            icon="bin-minus"
            stroke-width="1.8"
            color="text-universal-fg-opposite"
            class="w-[24px] h-[24px] pointer-events-none"
          />
        </IonItemOption>
      </IonItemOptions>
    </IonItemSliding>
  </ion-list>
</template>

<script setup lang="ts">
import {
  IonList,
  IonItemSliding,
  IonItem,
  IonItemOption,
  IonItemOptions,
  IonCheckbox,
} from '@ionic/vue'
import { ref, computed, type ComponentPublicInstance } from 'vue'
import { vOnClickOutside } from '@vueuse/components'
import AppIcon from '@restify/packages/design-system/low-level/AppIcon.vue'
import { useInjectMoveItemsState } from '@restify/packages/composables/useMoveItemsState'
import OrderModalHomeMenuitemsListItem from './OrderModalHomeMenuitemsListItem.vue'
import { type ComponentProps } from '@restify/packages/types'

type OrderModelHomeMenuitemsListProps = ComponentProps<
  typeof OrderModalHomeMenuitemsListItem
>

const emit = defineEmits<{
  'increase-quantity': [index: number],
  'decrease-quantity': [index: number],
  'add-comment': [payload: { index: number, callback: () => void }],
  'status-change': [payload: { status: string, id: string }],
  'delete': [id: string],
  'tap': [id: string],
}>()
const props = defineProps<{
  items?: Array<OrderModelHomeMenuitemsListProps & { _id: string }>
  orderId?: string
  quantityChangable?: boolean
  statusChangable?: boolean
  deletable?: boolean
}>()

const {
  moveItemsSourceOrderId,
  moveItemsIsSelected,
  toggleItem: moveItemsToggle,
} = useInjectMoveItemsState()

const selectMode = computed(
  () => !!props.orderId && moveItemsSourceOrderId.value === props.orderId,
)

const onSelectToggle = (id: string) => moveItemsToggle(id)

const onRowClick = (item: NonNullable<(typeof props.items)>[number]) => {
  if (selectMode.value) {
    moveItemsToggle(item._id)

    return
  }

  emit('tap', item._id)
}

const listRef = ref<(ComponentPublicInstance & { $el: HTMLElement }) | null>(
  null,
)

const onDoubleClick = (item: NonNullable<(typeof props.items)>[number]) => {
  return isStatusChangable(item) ? onStatusChange(item) : null
}

const isStatusChangable = (item: NonNullable<(typeof props.items)>[number]) => {
  return props.statusChangable && item.status === 'ready'
}

const onDelete = (item: NonNullable<(typeof props.items)>[number], $event: MouseEvent) => {
  if (!props.deletable) return

  const ionItem =
    $event.target instanceof HTMLElement &&
    $event.target.parentElement instanceof HTMLElement &&
    $event.target.parentElement.parentElement instanceof HTMLElement
      ? $event.target.parentElement.parentElement.querySelector('.item') as HTMLElement
      : null

  if (!ionItem) return

  ionItem.style.height = `${ionItem.clientHeight}px`
  ionItem.style.transition = 'height 200ms cubic-bezier(.68,.56,0,1)'
  getComputedStyle(ionItem).height

  requestAnimationFrame(() => {
    ionItem.style.height = '0px'

    setTimeout(() => emit('delete', item._id), 200)
  })
}

const onStatusChange = (item: NonNullable<(typeof props.items)>[number]) => {
  if (!isStatusChangable(item)) return

  emit('status-change', {
    status: 'served',
    id: item._id,
  })

  listRef.value?.$el?.closeSlidingItems()
}

const onAddComment = (index: number) => {
  emit('add-comment', { index, callback: () => {
    listRef.value?.$el?.closeSlidingItems()
  }})
}

const onClickOutside = () => {
  if (!props.quantityChangable && !props.deletable && props.statusChangable) {
    return
  }

  return listRef.value?.$el?.closeSlidingItems()
}

const onFullSwipe = (item: NonNullable<(typeof props.items)>[number]) => {
  return onStatusChange(item)
}
</script>

<style scoped>
.item {
  overflow: hidden;
  will-change: height, transform;
  backface-visibility: hidden;
  perspective: 1000px;
}
</style>
